import { useState, useCallback, useEffect } from 'react';
import type { ReactElement } from 'react';
import { Routes, Route, Navigate, NavLink, matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  isRtl,
  isSupportedLocale,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from '@noxverify/i18n';
import { ADMIN_NAV, getVisibleNavItems, type AdminModule } from './app-shell';
import { ADMIN_ROUTES } from './routes';
import { AuthProvider, useAuth } from './lib/auth-context';
import { ProjectProvider } from './lib/project-context';
import { ProjectSelector } from './components/ProjectSelector';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { LoginPage } from './pages/LoginPage';
import { TenantsPage } from './pages/TenantsPage';
import { PlatformMembersPage } from './pages/PlatformMembersPage';
import { SupportSessionsPage } from './pages/SupportSessionsPage';
import { AuditEventsPage } from './pages/AuditEventsPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { DomainsPage } from './pages/DomainsPage';
import { MembersPage } from './pages/MembersPage';
import { SsoPage } from './pages/SsoPage';
import { ConfigPromotionsPage } from './pages/ConfigPromotionsPage';
import { CasesPage } from './pages/CasesPage';
import { CaseDossierPage } from './pages/CaseDossierPage';
import { ReviewQueuePage } from './pages/ReviewQueuePage';
import { WebhooksPage } from './pages/WebhooksPage';
import { KybReviewPage } from './pages/KybReviewPage';
import { MonitoringDashboardPage } from './pages/MonitoringDashboardPage';
import { AnalyticsDashboardPage } from './pages/AnalyticsDashboardPage';
import { AlertsPage } from './pages/AlertsPage';
import { MerchantReviewPage } from './pages/MerchantReviewPage';
import { AiAuditPage } from './pages/AiAuditPage';
import { RunbooksPage } from './pages/RunbooksPage';
import { DataGovernancePage } from './pages/DataGovernancePage';
import { ReleasesPage } from './pages/ReleasesPage';

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, logout } = useAuth();
  const userPermissions = user?.permissions ?? [];
  const modules: AdminModule[] = ['platform', 'tenant-admin', 'analyst', 'operations', 'enterprise'];
  const moduleLabels: Record<AdminModule, string> = {
    'platform': 'Platform',
    'tenant-admin': 'Tenant Admin',
    'analyst': 'Analyst',
    'operations': 'Operations',
    'enterprise': 'Enterprise',
  };

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 start-0 z-40 w-64 bg-ink-surface text-ink-invert flex flex-col transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:z-auto ${
          open ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'
        }`}
      >
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="flex items-center gap-2.5 mb-8 px-2 pt-1">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 36 36" fill="none" aria-hidden="true">
                <path d="M11 25.5V10.5L25 25.5V10.5" stroke="white" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="font-display text-lg text-ink-invert">NoxVerify</span>
          </div>

          {modules.map(mod => {
            const visibleItems = getVisibleNavItems(mod, userPermissions);
            if (visibleItems.length === 0) return null;
            return (
              <div key={mod} className="mb-6">
                <h3 className="font-mono text-[0.6875rem] uppercase text-footer-muted px-3 mb-2 tracking-[0.12em]">{moduleLabels[mod]}</h3>
                <nav className="space-y-0.5">
                  {visibleItems.map(item => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `block px-3 py-2 rounded-sm text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface ${isActive ? 'bg-primary text-white font-medium border-s-2 border-primary-light' : 'text-footer-muted hover:bg-ink-surface2 hover:text-ink-invert'}`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              </div>
            );
          })}
        </div>

        {/* User info + logout */}
        {user && (
          <div className="p-4 border-t border-hairline-dark">
            <div className="flex items-center gap-3 px-2 mb-3">
              <div className="w-8 h-8 rounded-sm bg-ink-surface2 flex items-center justify-center text-sm font-medium text-primary-light">
                {user.displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink-invert truncate">{user.displayName}</div>
                <div className="text-xs text-footer-muted truncate font-mono">{user.email}</div>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full px-3 py-1.5 text-xs text-footer-muted hover:text-ink-invert hover:bg-ink-surface2 rounded-sm transition-colors text-start focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface"
            >
              Sign out
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

function ScopeIndicator({ onMenuToggle }: { onMenuToggle: () => void }) {
  const location = useLocation();
  const currentNav = ADMIN_NAV.find(n => location.pathname.startsWith(n.path));
  const currentRoute = ADMIN_ROUTES.find(definition => matchPath({ path: definition.path, end: true }, location.pathname));
  const module = currentNav?.module ?? currentRoute?.module ?? 'Unknown';
  const label = currentNav?.label ?? (currentRoute ? formatPageKey(currentRoute.pageKey) : 'NoxVerify Admin');

  return (
    <header className="bg-surface-raised border-b border-hairline px-4 py-3 flex items-center justify-between lg:px-6">
      <div className="flex items-center gap-3">
        {/* Hamburger button — mobile only */}
        <button
          type="button"
          onClick={onMenuToggle}
          className="p-1.5 -ms-1.5 text-ink-muted hover:text-ink lg:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
          aria-label="Open navigation menu"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>

        <nav aria-label="Breadcrumb" className="flex items-center gap-2.5">
          <span className="font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-ink-muted">
            {module}
          </span>
          <span className="text-hairline-strong font-mono" aria-hidden="true">/</span>
          <span className="text-sm font-semibold text-ink">
            {label}
          </span>
        </nav>
      </div>
      <div className="flex items-center gap-3">
        <LanguageSwitcher />
        <ProjectSelector />
      </div>
    </header>
  );
}

function formatPageKey(pageKey: string): string {
  return pageKey
    .split('-')
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function userCanAccess(requiredPermission: string, permissions: string[]): boolean {
  return permissions.includes('*') || permissions.includes(requiredPermission);
}

function GuardedRoute({ children }: { children: ReactElement }) {
  const { user } = useAuth();
  const location = useLocation();
  const permissions = user?.permissions ?? [];
  const route = ADMIN_ROUTES.find(definition => matchPath({ path: definition.path, end: true }, location.pathname));

  if (route && !userCanAccess(route.requiredPermission, permissions)) {
    return <NotAuthorizedPage requiredPermission={route.requiredPermission} />;
  }

  return children;
}

function DefaultRouteRedirect() {
  const { user } = useAuth();
  const permissions = user?.permissions ?? [];
  const firstVisibleNavItem = ADMIN_NAV.find(item => userCanAccess(item.permission, permissions));

  if (!firstVisibleNavItem) {
    return <NotAuthorizedPage />;
  }

  return <Navigate to={firstVisibleNavItem.path} replace />;
}

function withRouteGuard(element: ReactElement) {
  return <GuardedRoute>{element}</GuardedRoute>;
}

function AuthenticatedApp() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleMenuToggle = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const handleSidebarClose = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  return (
    <ProjectProvider>
      <div className="flex min-h-screen bg-surface">
        <Sidebar open={sidebarOpen} onClose={handleSidebarClose} />
        <div className="flex-1 flex flex-col min-w-0">
          <ScopeIndicator onMenuToggle={handleMenuToggle} />
          <main className="flex-1">
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<DefaultRouteRedirect />} />
                <Route path="/platform/tenants" element={withRouteGuard(<TenantsPage />)} />
                <Route path="/platform/members" element={withRouteGuard(<PlatformMembersPage />)} />
                <Route path="/platform/support-sessions" element={withRouteGuard(<SupportSessionsPage />)} />
                <Route path="/platform/audit-events" element={withRouteGuard(<AuditEventsPage />)} />
                <Route path="/tenant/projects" element={withRouteGuard(<ProjectsPage />)} />
                <Route path="/tenant/domains" element={withRouteGuard(<DomainsPage />)} />
                <Route path="/tenant/members" element={withRouteGuard(<MembersPage />)} />
                <Route path="/tenant/sso" element={withRouteGuard(<SsoPage />)} />
                <Route path="/tenant/config" element={withRouteGuard(<ConfigPromotionsPage />)} />
                <Route path="/tenant/cases" element={withRouteGuard(<CasesPage />)} />
                <Route path="/tenant/webhooks" element={withRouteGuard(<WebhooksPage />)} />
                <Route path="/cases/:caseId" element={withRouteGuard(<CaseDossierPage />)} />
                <Route path="/analyst/queue" element={withRouteGuard(<ReviewQueuePage />)} />
                <Route path="/analyst/kyb-review/:entityId" element={withRouteGuard(<KybReviewPage />)} />
                <Route path="/analyst/merchants/:profileId" element={withRouteGuard(<MerchantReviewPage />)} />
                <Route path="/operations/monitoring" element={withRouteGuard(<MonitoringDashboardPage />)} />
                <Route path="/operations/analytics" element={withRouteGuard(<AnalyticsDashboardPage />)} />
                <Route path="/operations/alerts" element={withRouteGuard(<AlertsPage />)} />
                <Route path="/operations/ai-audit" element={withRouteGuard(<AiAuditPage />)} />
                <Route path="/enterprise/runbooks" element={withRouteGuard(<RunbooksPage />)} />
                <Route path="/enterprise/data-governance" element={withRouteGuard(<DataGovernancePage />)} />
                <Route path="/enterprise/releases" element={withRouteGuard(<ReleasesPage />)} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </ProjectProvider>
  );
}

function NotFoundPage() {
  return (
    <div className="p-6">
      <h1 className="font-display text-2xl text-ink mb-4">Not Found</h1>
      <p className="text-ink-muted">The page you are looking for does not exist.</p>
    </div>
  );
}

function NotAuthorizedPage({ requiredPermission }: { requiredPermission?: string }) {
  return (
    <div className="p-6">
      <div className="max-w-2xl rounded-sm border border-warning/30 bg-warning-tint p-5 text-ink" role="alert">
        <h1 className="text-xl font-semibold text-ink">Not authorized</h1>
        <p className="mt-2 text-sm text-ink-muted">
          You do not have permission to view this admin page. If you need access, contact your administrator.
        </p>
        {requiredPermission && (
          <p className="mt-3 text-xs text-ink-muted">
            Required permission: <code className="font-mono text-warning">{requiredPermission}</code>
          </p>
        )}
      </div>
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

function AppRouter() {
  const { isAuthenticated, loading } = useAuth();
  const { i18n } = useTranslation();

  // Keep <html lang> and dir in sync with the active locale.
  // ADMIN_LOCALES has no RTL members today, but we stay defensive so future
  // additions (or a misconfiguration) don't silently break layout.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const lng = isSupportedLocale(i18n.language)
      ? (i18n.language as SupportedLocale)
      : DEFAULT_LOCALE;
    document.documentElement.lang = lng;
    document.documentElement.dir = isRtl(lng) ? 'rtl' : 'ltr';
  }, [i18n.language]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"
            role="status"
            aria-label="Authenticating"
          />
          <p className="text-sm text-ink-muted">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <AuthenticatedApp />;
}
